import { useEffect, useState } from "react";
import moment from "moment";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import {
  TournamentMatchStageEnum,
  TournamentMatchResultEnum,
} from "../../../constants/appConstant";
import { AppInput, AppSearchSelect } from "../../../components/appComponents";
import Card from "../../../components/ui/card-snippet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import Utility from "../../../helper/utilityHelper";
import { ExportAsExcel, ExportAsPdf } from "react-export-table";
import { NiExcel, NiPdf } from "../../../svg";

export default function ViewSchedules() {
  const [masterTournaments, setMasterTournaments] = useState([]);
  const [selectedMasterTournament, setSelectedMasterTournament] =
    useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournaments, setSelectedTournaments] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterDate, setFilterDate] = useState(null);
  const [filterCourt, setFilterCourt] = useState(null);
  const [filterTournament, setFilterTournament] = useState(null);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (selectedMasterTournament) {
      loadTournaments();
    } else {
      setTournaments([]);
      setSelectedTournaments([]);
      setAllMatches([]);
      setFilteredMatches([]);
      setCourts([]);
    }
  }, [selectedMasterTournament]);

  useEffect(() => {
    if (selectedTournaments.length > 0) {
      loadMatches();
    } else {
      setAllMatches([]);
      setFilteredMatches([]);
    }
  }, [selectedTournaments]);

  useEffect(() => {
    applyFilters();
  }, [filterDate, filterCourt, filterTournament, allMatches]);

  const initialize = async () => {
    try {
      setLoading(true);
      const response = await Common.ApiService.getInstance().request(
        `GetMasterTournaments?id=&sportId=`,
        null,
        "GET"
      );
      if (response?.data) {
        const data = [
          { id: null, name: "Select Master Tournament" },
          ...(response.data || []),
        ];
        setMasterTournaments(data);
      }
    } catch (error) {
      console.error("Error loading master tournaments:", error);
      Common.showToast("Error loading master tournaments", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const response = await Common.ApiService.getInstance().request(
        `GetTournaments`,
        {
          masterTournamentId: selectedMasterTournament,
          pageNumber: 1,
          recordsToDisplay: 1000, // Get all tournaments
        },
        "POST"
      );
      if (response?.data?.[0]?.data) {
        const tournamentList = response.data[0].data;
        setTournaments(tournamentList);
        // Auto-select all tournaments
        setSelectedTournaments(tournamentList.map((t) => t.id));
      }
    } catch (error) {
      console.error("Error loading tournaments:", error);
      Common.showToast("Error loading tournaments", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await Common.ApiService.getInstance().request(
        `GetTournamentScheduleMatches`,
        {
          tournamentIds: selectedTournaments,
        },
        "POST"
      );
      if (response?.data) {
        const matches = response.data.map((match) => ({
          ...match,
          typetournament: match.stageType,
          team1: match.teamAName,
          team2: match.teamBName,
          venueId: match.clubId
            ? match.clubId
            : match.cityId
            ? match.cityId
            : null,
          venueName: match.clubName || match.cityName || null,
          courtId: match.courtId,
          courtName: match.courtName,
          startDateTime: match.startDateTime,
          group1: match.group1,
          group2: match.group2,
          tournamentName: match.tournamentName,
        }));

        setAllMatches(matches);

        // Extract unique courts for filter
        const uniqueCourts = [
          ...new Map(
            matches
              .filter((m) => m.courtId && m.courtName)
              .map((m) => [m.courtId, { id: m.courtId, name: m.courtName }])
          ).values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        setCourts([{ id: null, name: "All Courts" }, ...uniqueCourts]);
      }
    } catch (error) {
      console.error("Error loading matches:", error);
      Common.showToast("Error loading matches", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allMatches];

    // Filter by date
    if (filterDate) {
      filtered = filtered.filter((match) => {
        if (!match.startDateTime) return false;
        try {
          // Parse both dates and compare only the date part (ignore time)
          const matchDate = moment(match.startDateTime);
          const filterDateMoment = moment(filterDate);

          // Check if both dates are valid
          if (!matchDate.isValid() || !filterDateMoment.isValid()) {
            return false;
          }

          // Compare dates by formatting to YYYY-MM-DD (this ignores time and timezone)
          const matchDateStr = matchDate.format("YYYY-MM-DD");
          const filterDateStr = filterDateMoment.format("YYYY-MM-DD");

          return matchDateStr === filterDateStr;
        } catch (error) {
          console.error(
            "Date filter error:",
            error,
            match.startDateTime,
            filterDate
          );
          return false;
        }
      });
    }

    // Filter by court
    if (filterCourt) {
      filtered = filtered.filter((match) => match.courtId === filterCourt);
    }

    // Filter by tournament
    if (filterTournament) {
      filtered = filtered.filter((match) => {
        const tournament = tournaments.find((t) => t.id === filterTournament);
        return tournament && match.tournamentName === tournament.name;
      });
    }

    setFilteredMatches(filtered);
  };

  const resetFilters = () => {
    setFilterDate(null);
    setFilterCourt(null);
    setFilterTournament(null);
  };

  const handleExportExcel = () => {
    const exportData = filteredMatches.map((match) => ({
      Tournament: match.tournamentName || "-",
      "Match Type":
        Common.Utility.getKeyByValue(
          TournamentMatchStageEnum,
          match.stageType
        ) || "-",
      "Team 1": match.team1 || "-",
      "Team 2": match.team2 || "-",
      Venue: match.venueName || "-",
      Court: match.courtName || "-",
      "Schedule Date": match.startDateTime
        ? Utility.formatDateTime(match.startDateTime)
        : "-",
      Played: match.isPlayed ? "Yes" : "No",
      "Result Uploaded": match.isResultUploaded ? "Yes" : "No",
      "Match Result":
        Common.Utility.getKeyByValue(
          TournamentMatchResultEnum,
          match.matchResult
        ) || "No Result",
    }));

    return exportData;
  };

  const handleExportPDF = () => {
    const exportData = filteredMatches.map((match) => ({
      Tournament: match.tournamentName || "-",
      "Match Type":
        Common.Utility.getKeyByValue(
          TournamentMatchStageEnum,
          match.stageType
        ) || "-",
      "Team 1": match.team1 || "-",
      "Team 2": match.team2 || "-",
      Venue: match.venueName || "-",
      Court: match.courtName || "-",
      "Schedule Date": match.startDateTime
        ? Utility.formatDateTime(match.startDateTime)
        : "-",
      Played: match.isPlayed ? "Yes" : "No",
      "Result Uploaded": match.isResultUploaded ? "Yes" : "No",
      "Match Result":
        Common.Utility.getKeyByValue(
          TournamentMatchResultEnum,
          match.matchResult
        ) || "No Result",
    }));

    return exportData;
  };

  const exportHeaders = [
    "Tournament",
    "Match Type",
    "Team 1",
    "Team 2",
    "Venue",
    "Court",
    "Schedule Date",
    "Played",
    "Result Uploaded",
    "Match Result",
  ];

  return (
    <>
      <style>
        {`
          .custom-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border: 2px solid #e2e8f0;
            border-radius: 16px;
            box-shadow: 0 10px 25px rgba(12, 27, 58, 0.1);
            transition: all 0.3s ease;
          }

          .custom-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 35px rgba(12, 27, 58, 0.15);
            border-color: #2E55B9;
          }

          .filter-card {
            background: linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%);
            border-radius: 16px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 8px 25px rgba(12, 27, 58, 0.2);
          }

          .filter-card label {
            color: white !important;
          }

          .filter-card .text-gray-700,
          .filter-card .text-gray-800,
          .filter-card .text-gray-900 {
            color: white !important;
          }

          .match-table {
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(12, 27, 58, 0.08);
          }

          .table-header {
            background: linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%);
            color: white !important;
          }

          .table-header th {
            color: white !important;
            font-weight: 600;
          }

          .group-badge {
            background: linear-gradient(135deg, #2E55B9 0%, #0C1B3A 100%);
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }
        `}
      </style>

      <div className="space-y-6 mb-3">
        <Card
          title={
            <div className="text-center text-2xl font-bold text-[#0C1B3A]">
              📅 View Tournament Schedules
            </div>
          }
          className="custom-card"
        >
          <div className="p-5">
            <div className="mb-6">
              <AppSearchSelect
                data={masterTournaments}
                placeholder="Select Master Tournament"
                value={selectedMasterTournament}
                label="Master Tournament"
                onChange={(val) => {
                  setSelectedMasterTournament(val);
                  resetFilters();
                }}
                disabled={loading}
              />
            </div>

            {selectedMasterTournament && tournaments.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tournaments ({tournaments.length} found)
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border-2 border-gray-200 max-h-40 overflow-y-auto">
                  {tournaments.map((tournament) => (
                    <div
                      key={tournament.id}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        selectedTournaments.includes(tournament.id)
                          ? "bg-gradient-to-r from-[#2E55B9] to-[#0C1B3A] text-white"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {tournament.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {allMatches.length > 0 && (
          <div className="filter-card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold flex items-center">
                🔍 Filters & Search
              </h3>
              <div className="flex gap-2">
                <ExportAsExcel
                  data={handleExportExcel()}
                  headers={exportHeaders}
                  fileName={`Tournament_Schedules_${moment().format(
                    "YYYY-MM-DD_HHmm"
                  )}`}
                >
                  {(props) => (
                    <Button
                      {...props}
                      className="bg-white text-[#0C1B3A] hover:bg-gray-100 font-semibold border-2 border-white"
                    >
                      <NiExcel className="mr-2" />
                      Excel
                    </Button>
                  )}
                </ExportAsExcel>
                <ExportAsPdf
                  data={handleExportPDF()}
                  headers={exportHeaders}
                  fileName={`Tournament_Schedules_${moment().format(
                    "YYYY-MM-DD_HHmm"
                  )}`}
                  headerStyles={{ fillColor: [12, 27, 58] }}
                >
                  {(props) => (
                    <Button
                      {...props}
                      className="bg-white text-[#0C1B3A] hover:bg-gray-100 font-semibold border-2 border-white"
                    >
                      <NiPdf className="mr-2" />
                      PDF
                    </Button>
                  )}
                </ExportAsPdf>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <div className="flex flex-col">
                <AppInput
                  type="date"
                  value={filterDate}
                  onChange={setFilterDate}
                  label="Filter by Date"
                  className="mt-2"
                />
              </div>

              <div className="flex flex-col">
                <AppSearchSelect
                  data={courts}
                  placeholder="All Courts"
                  value={filterCourt}
                  label="Filter by Court"
                  onChange={setFilterCourt}
                  className="mt-2"
                />
              </div>

              <div className="flex flex-col">
                <AppSearchSelect
                  data={[
                    { id: null, name: "All Tournaments" },
                    ...tournaments.map((t) => ({
                      id: t.id,
                      name: t.name,
                    })),
                  ]}
                  placeholder="All Tournaments"
                  value={filterTournament}
                  label="Filter by Tournament"
                  onChange={setFilterTournament}
                  className="mt-2"
                />
              </div>

              <div className="flex flex-col">
                <Button
                  className="mt-9 h-10 max-w-[120px] bg-white text-[#0C1B3A] hover:bg-gray-100 font-semibold border-2 border-white"
                  onClick={resetFilters}
                >
                  🔄 Reset
                </Button>
              </div>
            </div>
          </div>
        )}

        {filteredMatches.length === 0 && allMatches.length > 0 && (
          <div className="flex flex-col items-center justify-center mt-6 p-4">
            <h2 className="text-xl font-semibold text-gray-700">
              No Records Found
            </h2>
            <p className="text-gray-600 mt-1">
              No matches found based on your current filters.
            </p>
          </div>
        )}

        {filteredMatches.length > 0 && (
          <div className="custom-card match-table">
            <div className="text-center text-white font-bold text-2xl bg-gradient-to-r from-[#0C1B3A] to-[#2E55B9] p-6 rounded-t-xl mb-0">
              📊 Match Schedules ({filteredMatches.length} matches)
            </div>
            <Card className="rounded-t-none">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="table-header">
                    <TableRow>
                      <TableHead style={{ textAlign: "center" }}>
                        Tournament
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Match Type
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Team 1
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>VS</TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Team 2
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Venue
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Court
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Schedule Date
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Played
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Result Uploaded
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        Match Result
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMatches.map((match, index) => (
                      <TableRow key={index}>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.tournamentName || "-"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {Common.Utility.getKeyByValue(
                            TournamentMatchStageEnum,
                            match.stageType
                          )?.replace(/_/g, " ") || "-"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.team1 || "-"}
                          {match.group1 && (
                            <>
                              <br />
                              <span className="group-badge">
                                (Group {match.group1})
                              </span>
                            </>
                          )}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          VS
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.team2 || "-"}
                          {match.group2 && (
                            <>
                              <br />
                              <span className="group-badge">
                                (Group {match.group2})
                              </span>
                            </>
                          )}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.venueName || "-"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.courtName || "-"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.startDateTime
                            ? Utility.formatDateTime(match.startDateTime)
                            : "-"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.isPlayed ? "Yes" : "No"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {match.isResultUploaded ? "Yes" : "No"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {Common.Utility.getKeyByValue(
                            TournamentMatchResultEnum,
                            match.matchResult
                          )?.replace(/_/g, " ") || "No Result"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        )}

        {!selectedMasterTournament && (
          <div className="flex flex-col items-center justify-center mt-6 p-4">
            <h2 className="text-xl font-semibold text-gray-700">
              Select a Master Tournament
            </h2>
            <p className="text-gray-600 mt-1">
              Please select a master tournament to view schedules.
            </p>
          </div>
        )}

        {selectedMasterTournament && allMatches.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center mt-6 p-4">
            <h2 className="text-xl font-semibold text-gray-700">
              No Matches Found
            </h2>
            <p className="text-gray-600 mt-1">
              No scheduled matches found for the selected tournaments.
            </p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center mt-6 p-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E55B9]"></div>
            <p className="text-gray-600 mt-4">Loading...</p>
          </div>
        )}
      </div>
    </>
  );
}
